/*
  COCO RLE decode.

  Input shape: { counts: number[], size: [height, width] }
  - counts: alternating run-lengths in column-major (Fortran) order
    c0 = background, c1 = foreground, c2 = background, ...
  - size:   [H, W]

  Output: { mask: Uint8Array (row-major, length H*W), width, height }

  Caching: results are memoized by the rle *object identity* in a module-scoped
  WeakMap. Frame scrubbing across already-propagated frames becomes O(1) decode
  because propagatedMasks reuses the same rle object until the user re-runs
  propagation or edits the frame. When the component replaces the rle, the
  WeakMap drops the entry naturally on GC.

  Pass a freshly-constructed { counts, size } object (common at one-off call
  sites like "apply automask") and you get the same correct output, just no
  cache benefit. That is the intended trade-off: the cache helps the hot path
  (frame switching) without slowing the cold path.
*/

const decodeCache = new WeakMap();

function decodeRaw(rle) {
  const [height, width] = rle.size;
  const counts = rle.counts;
  const totalPixels = height * width;

  const maskColMajor = new Uint8Array(totalPixels);
  let pos = 0;
  for (let i = 0; i < counts.length; i++) {
    const count = counts[i];
    if (i % 2 === 1) {
      const end = Math.min(count, totalPixels - pos);
      for (let j = 0; j < end; j++) maskColMajor[pos + j] = 1;
    }
    pos += count;
  }

  // Transpose column-major (Fortran) → row-major (C) for canvas rendering.
  const mask = new Uint8Array(totalPixels);
  for (let col = 0; col < width; col++) {
    for (let row = 0; row < height; row++) {
      mask[row * width + col] = maskColMajor[col * height + row];
    }
  }
  return { mask, width, height };
}

export function decodeCOCORLE(rle) {
  if (!rle || typeof rle !== 'object' || !rle.counts || !Array.isArray(rle.size)) {
    return null;
  }
  const hit = decodeCache.get(rle);
  if (hit) return hit;
  try {
    const decoded = decodeRaw(rle);
    decodeCache.set(rle, decoded);
    return decoded;
  } catch (err) {
    return null;
  }
}
